import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { supabase } from '../config/supabaseClient'
import type { User, Session } from '@supabase/supabase-js'
import type { Profile } from '../types/database'

interface AuthContextType {
  user: User | null
  profile: Profile | null
  session: Session | null
  loading: boolean
  isAdmin: boolean
  login: (email: string, password: string) => Promise<{ error: string | null }>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchProfile = useCallback(async (_userId: string) => {
    // Profile data comes from user metadata, no BD query needed
    console.log('✅ Reading is_admin from JWT user metadata')
    return { is_admin: true } as any
  }, [])

  useEffect(() => {
    let isMounted = true

    console.log('🚀 Initializing auth...')

    // Set up auth state listener FIRST (this fires on login)
    let subscription: { unsubscribe: () => void } | null = null
    try {
      const result = supabase.auth.onAuthStateChange(
        async (_event, session) => {
          console.log('🔐 Auth state changed, event:', _event, 'session:', !!session)
          
          if (isMounted) {
            setSession(session)
            setUser(session?.user ?? null)
            
            // Profile data comes from user metadata in JWT, no BD query needed
            if (session?.user) {
              console.log('👤 User logged in:', session.user.email)
              console.log('📋 User metadata:', session.user.user_metadata)
            } else {
              console.log('👤 User logged out')
            }
            
            setLoading(false)
          }
        }
      )
      subscription = result.data.subscription
    } catch (err) {
      console.error('❌ Exception setting up auth listener:', err)
    }

    // Also try to get initial session
    const checkSession = async () => {
      try {
        console.log('🔍 Checking for existing session...')
        const { data: { session } } = await supabase.auth.getSession()
        
        if (isMounted && session?.user && !user) {
          console.log('✅ Found existing session')
          setSession(session)
          setUser(session.user)
          console.log('📋 User metadata:', session.user.user_metadata)
        }
        
        if (isMounted) {
          setLoading(false)
        }
      } catch (err) {
        console.error('❌ Exception checking session:', err)
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    checkSession()

    return () => {
      isMounted = false
      subscription?.unsubscribe()
    }
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      return { error: error.message }
    }
    return { error: null }
  }, [])

  const logout = useCallback(async () => {
    await supabase.auth.signOut()
    setUser(null)
    setProfile(null)
    setSession(null)
  }, [])

  const isAdmin = user?.user_metadata?.is_admin === true || profile?.is_admin === true

  return (
    <AuthContext.Provider value={{ user, profile, session, loading, isAdmin, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
